import { Component, inject, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { PlatformDetectionService } from '../../../../../shared/services/platform-detection.service';
import { PlaceholderComponent } from '../../../../../shared/components/placeholder/placeholder.component';
import { AppButtonComponent } from '../../../../../shared/core/ui';
import { RiyalSymbolComponent } from '../../../../../shared/core/ui/riyal-symbol/riyal-symbol.component';
import { ErrorOrError } from '../../../../../common/core/results';
import { handleApiErrors } from '../../../../../common/core/data-access/http-client';
import { extractLastPathFromUrl } from '../../../../../common/utils/url.utils';
import { ToastService } from '../../../../../trash/shared/components/toasters/Toast.service';
import { CustomerProfilesApiClientProvider, ICustomerProfilesApiClient } from '../../../clients';

type DialogMode = 'scan' | 'manual';

@Component({
  selector: 'app-add-parking-ticket-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    PlaceholderComponent,
    AppButtonComponent,
    RiyalSymbolComponent,
    InputTextModule,
    SkeletonModule,
  ],
  templateUrl: './add-parking-ticket-dialog.component.html',
  styleUrl: './add-parking-ticket-dialog.component.scss',
})
export class AddParkingTicketDialogComponent implements OnDestroy {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly platformDetection = inject(PlatformDetectionService);
  private client: ICustomerProfilesApiClient = inject(CustomerProfilesApiClientProvider).getClient();
  readonly isMobile = this.platformDetection.isMobile();
  private toastService: ToastService = inject(ToastService);
  activeMode = signal<DialogMode>('manual');
  ticketNumber = '';
  isLoading = signal(false);
  foundTicket = signal<any>(null);
  scannerError = signal<string | null>(null);
  scannerActive = signal(false);
  lookupErrorKey = signal<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private qrCode: any = null;

  setMode(mode: DialogMode): void {
    if (mode === this.activeMode()) return;
    this.stopScanner();
    this.activeMode.set(mode);
    this.foundTicket.set(null);
    this.lookupErrorKey.set(null);

    if (mode === 'scan' && this.isMobile) {
      setTimeout(() => this.startScanner(), 80);
    }
  }

  private startScanner(): void {
    this.scannerError.set(null);
    this.scannerActive.set(false);

    try {
      // Dynamically import to avoid SSR issues
      import('html5-qrcode').then(({ Html5Qrcode }) => {
        this.qrCode = new Html5Qrcode('qr-scanner-container');
        this.qrCode
          .start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: { width: 240, height: 240 } },
            (decodedText: string) => this.onScanSuccess(decodedText),
            () => {
              /* ignore individual frame decode errors */
            }
          )
          .then(() => {
            this.scannerActive.set(true);
          })
          .catch((err: any) => {
            const msg = (err?.message ?? '').toLowerCase();
            if (msg.includes('permission') || msg.includes('notallowed') || msg.includes('denied')) {
              this.scannerError.set('ADD_TICKET.SCANNER_PERMISSION_ERROR');
            } else {
              this.scannerError.set('ADD_TICKET.SCANNER_ERROR');
            }
          });
      });
    } catch {
      this.scannerError.set('ADD_TICKET.SCANNER_ERROR');
    }
  }

  private onScanSuccess(decodedText: string): void {
    this.stopScanner();
    this.ticketNumber = extractLastPathFromUrl(decodedText);
    this.activeMode.set('manual');
    if (!this.isTicketNumberValidFormat()) {
      return;
    }
    this.lookupTicket();
  }

  onTicketNumberChange(value: string): void {
    const normalizedValue = extractLastPathFromUrl(value);
    this.ticketNumber = this.shouldNormalizeManualTicketInput(value, normalizedValue) ? normalizedValue : value;
    this.lookupErrorKey.set(null);
  }

  private shouldNormalizeManualTicketInput(value: string, normalizedValue: string): boolean {
    return normalizedValue !== value.trim() && normalizedValue.toUpperCase().startsWith('PT-');
  }

  private mapLookupErrorCodeToTranslationKey(code?: string): string | null {
    switch (code) {
      case 'OffStreetReservationErrors.AlreadyHasOwner':
        return 'ADD_TICKET.ERROR_ALREADY_HAS_OWNER_BODY';
      case 'OffStreetReservations.NotFound':
        return 'ADD_TICKET.ERROR_OFF_STREET_NOT_FOUND_BODY';
      case 'CustomerProfiles.NotFound':
        return 'ADD_TICKET.ERROR_CUSTOMER_NOT_FOUND_BODY';
      case 'PartyTypeEnum.NotFound':
        return 'ADD_TICKET.ERROR_PARTY_TYPE_NOT_FOUND_BODY';
      default:
        return null;
    }
  }

  private stopScanner(): void {
    if (!this.qrCode) return;
    const ref = this.qrCode;
    this.qrCode = null;
    this.scannerActive.set(false);
    ref
      .stop()
      .catch(() => {})
      .finally(() => ref.clear().catch(() => {}));
  }

  /** Off-street parking ticket numbers must start with PT (case-insensitive). */
  isTicketNumberValidFormat(): boolean {
    const t = this.ticketNumber.trim();
    return t.length > 0 && t.toUpperCase().startsWith('PT');
  }

  lookupTicket(): void {
    if (!this.isTicketNumberValidFormat()) {
      return;
    }

    this.foundTicket.set(null);
    this.lookupErrorKey.set(null);
    this.isLoading.set(true);
    const ticketCode = this.ticketNumber.trim();

    this.client.pickOffStreetReservation(ticketCode).subscribe({
      next: () => {
        this.dialogRef.close(ticketCode);
      },
      error: (errors: ErrorOrError[]) => {
        const inlineErrorKey = this.mapLookupErrorCodeToTranslationKey(errors?.[0]?.code);
        if (inlineErrorKey) {
          this.lookupErrorKey.set(inlineErrorKey);
        } else {
          handleApiErrors(errors, this.toastService);
        }
        this.isLoading.set(false);
      },
      complete: () => this.isLoading.set(false)
    });
  }

  ngOnDestroy(): void {
    this.stopScanner();
  }
}
